/**
 * FixtureSelectOptions — the grouped <option> list for the game (fixture) picker
 * used by both the Propose and Record pages. Fixtures are grouped by division via
 * <optgroup> (the division is the group heading), and each option shows the
 * matchup "Team A vs Team B". Render inside a <select> after the placeholder
 * option.
 */
import React from 'react';
import type { Game } from '@sulis-liiga/common';

/** "Team A vs Team B" for a fixture (division is the optgroup heading). */
export const fixtureMatchupLabel = (f: Game): string => {
  const a = f.teamNames?.[f.teamIds[0]] ?? f.teamIds[0];
  const b = f.teamNames?.[f.teamIds[1]] ?? f.teamIds[1];
  return `${a} vs ${b}`;
};

const FixtureSelectOptions: React.FC<{ fixtures: Game[] }> = ({ fixtures }) => {
  // Group by division, preserving first-seen order.
  const groups = new Map<string, Game[]>();
  for (const f of fixtures) {
    const key = f.divisionName ?? 'Other games';
    const arr = groups.get(key) ?? [];
    arr.push(f);
    groups.set(key, arr);
  }

  return (
    <>
      {Array.from(groups.entries()).map(([division, games]) => (
        <optgroup key={division} label={division}>
          {games.map(f => (
            <option key={f.id} value={f.id}>
              {fixtureMatchupLabel(f)}
            </option>
          ))}
        </optgroup>
      ))}
    </>
  );
};

export default FixtureSelectOptions;
